import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {TasksGridProvider, useTasksGridContext} from './TasksGridContext';
import {useRouteMatch} from 'react-router-dom';
import {pathnames as P} from '../constants/pathnames';
import {NotesGridProvider, useNotesGridContext} from './NotesGridContext';
import moment from 'moment/moment';
import {taskTypeIDs} from '../constants/taskTypes';
import {useFormik} from 'formik';
import * as actions from '../actions/generalData';
import {NoteRow} from './NotesGridContext';
import {TaskRow} from './TasksGridContext';

interface TaskAndNotesGridProviderProps {
  children: ReactNode;
  shumaID: number;
}

interface TaskAndNotesGridInnerProviderProps {
  children: ReactNode;
  shumaID: number;
}

interface TaskAndNotesFormikValues {
  taskID: number,
  responsibleUserID: number,
  taskTypeID: number,
  taskStatusID: number,
  taskDueDate: Date,
  notes: string,
  taskDescription: string,
  visitContactName: string,
  visitContactPhone: string,

  generalNoteID: number,
  generalNoteResponsibleUserID: number,
  generalNoteDueDate: Date | null,
  generalNoteStatusTypeID: number,
  generalNoteFreeText: string,
}

const TaskAndNotesGridContext = createContext({});

export const useTaskAndNotesGridContext = () => useContext(TaskAndNotesGridContext);

const TaskAndNotesGridInnerProvider = ({children, shumaID}: TaskAndNotesGridInnerProviderProps) => {
  const tasksContext = useTasksGridContext();
  const {
    taskDetails,
    getTaskDetails,
    editVisitTask,
    editGeneralTask,
    deleteTask
  } = tasksContext;
  const notesContext = useNotesGridContext();
  const {
    noteDetails,
    getNoteDetails,
    editNote,
    deleteNote,
  } = notesContext;

  const updateList = () => {
    setTimeout(() => {
      tasksContext.updateList(shumaID);
      notesContext.updateList(shumaID);
    }, 2000);
  };

  const fieldNames = {
    responsibleUserID: 'responsibleUserID',
    taskTypeID: 'taskTypeID',
    taskStatusID: 'taskStatusID',
    taskDueDate: 'taskDueDate',
    notes: 'notes',
    taskDescription: 'taskDescription',
    visitContactName: 'visitContactName',
    visitContactPhone: 'visitContactPhone',

    generalNoteResponsibleUserID: 'generalNoteResponsibleUserID',
    generalNoteDueDate: 'generalNoteDueDate',
    generalNoteStatusTypeID: 'generalNoteStatusTypeID',
    generalNoteFreeText: 'generalNoteFreeText',
  };

  const initialValues = {
    taskID: taskDetails?.taskID || -1,
    responsibleUserID: taskDetails?.responsibleUserID,
    taskTypeID: taskDetails?.taskTypeID,
    taskStatusID: taskDetails?.taskStatusID || 1,
    taskDueDate: moment(taskDetails.dueDate).toDate(),
    notes: taskDetails?.notes || '',
    taskDescription: taskDetails?.taskDescription || '',
    visitContactName: taskDetails?.visitContactName || '',
    visitContactPhone: taskDetails?.visitContactPhone || '',

    generalNoteID: noteDetails?.generalNoteID || -1,
    generalNoteResponsibleUserID: noteDetails?.responsibleUserID,
    generalNoteDueDate: noteDetails?.dueDate && noteDetails.generalNoteID ? moment(noteDetails.dueDate).toDate() : null,
    generalNoteStatusTypeID: noteDetails?.statusTypeID,
    generalNoteFreeText: noteDetails?.freeText || '',
  };

  const validate = (values: TaskAndNotesFormikValues) => {
    let errors: any = {};
    if (values.taskTypeID) {
      const {
        taskTypeID,
        responsibleUserID,
        taskStatusID,
        taskDescription,
        taskDueDate,
      } = values;

      if (!responsibleUserID) errors[`responsibleUserID`] = 'Required field';
      if (!taskStatusID) errors[`taskStatusID`] = 'Required field';
      if (!taskDueDate) errors[`taskDueDate`] = 'Required field';

      if (taskTypeID === taskTypeIDs.general) {
        if (!taskDescription) errors[`taskDescription`] = 'Required field';
      }
    } else {
      if (!values.generalNoteStatusTypeID) errors['generalNoteStatusTypeID'] = 'Required field';
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: () => {
    },
    validate,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const list = [
    ...tasksContext.tasksList.map((item: TaskRow) => {
      return {
        ...item,
        onOpen: () => {
          formik.resetForm();
          getTaskDetails(item.taskID);
        },
        onEdit: () => {
          formik.values.taskTypeID === taskTypeIDs.visit
            ? editVisitTask(
              formik.values.taskID,
              formik.values.responsibleUserID,
              formik.values.taskStatusID,
              formik.values.taskDueDate,
              formik.values.notes,
              formik.values.visitContactName,
              formik.values.visitContactPhone,
            )
            : editGeneralTask(
              formik.values.taskID,
              formik.values.responsibleUserID,
              formik.values.taskStatusID,
              formik.values.taskDueDate,
              formik.values.taskDescription,
            );
          updateList();
        },
        onDelete: () => {
          deleteTask(item.taskID);
          updateList();
        }
      };
    }),
    ...notesContext.notesList.map((item: NoteRow) => {
      return {
        ...item,
        onOpen: () => {
          formik.resetForm();
          getNoteDetails(item.generalNoteID);
        },
        onEdit: () => {
          editNote(
            formik.values.generalNoteID,
            formik.values.generalNoteStatusTypeID,
            formik.values.generalNoteDueDate,
            formik.values.generalNoteResponsibleUserID,
            formik.values.generalNoteFreeText
          );
          updateList();
        },
        onDelete: async () => {
          deleteNote(item.generalNoteID);
          updateList();
        }
      };
    })
  ];

  const [generalNoteStatusTypes, setGeneralNoteStatusTypes] = useState([]);
  useEffect(() => {
    actions.getGeneralNoteStatusTypes()
      .then(setGeneralNoteStatusTypes)
      .catch((e) => {
        console.error(e);
        setGeneralNoteStatusTypes([]);
      });
  }, []);

  return (
    <TaskAndNotesGridContext.Provider value={{
      list,
      updateList,
      formik,
      fieldNames,
      disabled: !Boolean(shumaID) || (shumaID === -1),
      generalNoteStatusTypes,
    }}>
      {children}
    </TaskAndNotesGridContext.Provider>
  );
};

export const TaskAndNotesGridProvider = ({children, shumaID}: TaskAndNotesGridProviderProps) => {
  const match = useRouteMatch<{shumaID: string}>(P.NEW_INSPECTION_DETAILS);
  const currentShumaID = shumaID
    ? shumaID
    : match ? parseInt(match.params.shumaID) : 0;

  return (
    <TasksGridProvider shumaID={currentShumaID}>
      <NotesGridProvider shumaID={currentShumaID}>
        <TaskAndNotesGridInnerProvider shumaID={currentShumaID}>
          {children}
        </TaskAndNotesGridInnerProvider>
      </NotesGridProvider>
    </TasksGridProvider>
  );
};

